// ========================
// == Map pin scaling: pins grow as the map zooms in and shrink as it zooms out, clamped so
// == they never get silly small or large. Opt in with `data-pin-zoom-scale` on the map element.

/** Zoom level where pins render at their base `data-marker_icon_wh` size. @type {number} */
const BASE_ZOOM = 11;
/** Size change per zoom level, as a fraction of the base size. @type {number} */
const SCALE_PER_ZOOM = 0.08;
/** @type {number} */
const MIN_SCALE = 0.6;
/** @type {number} */
const MAX_SCALE = 1.1;

/**
 * @param {number} zoom
 * @returns {number}
 */
const pinScale = (zoom) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, 1 + (zoom - BASE_ZOOM) * SCALE_PER_ZOOM));

/**
 * @param {google.maps.Icon} icon
 * @param {number} width
 * @param {number} height
 * @returns {google.maps.Icon}
 */
const resizedIcon = (icon, width, height) => ({
    ...icon,
    size: new google.maps.Size(width, height),
    scaledSize: new google.maps.Size(width, height),
});

/**
 * Resize every pin on the map for its current zoom.
 * @param {any} mapWrap The oimap wrapper (`oi.loaded.map[id]`).
 * @returns {void}
 */
const scalePins = (mapWrap) => {
    const nativeMap = mapWrap.vendor_implementation?.map;
    if (!nativeMap) {
        return;
    }
    const [baseWidth, baseHeight] = mapWrap.marker_icon_wh.split(",").map(Number);
    const scale = pinScale(nativeMap.getZoom());
    const width = Math.round(baseWidth * scale);
    const height = Math.round(baseHeight * scale);

    mapWrap.markers.forEach((marker) => {
        // A hovered pin restores this icon on mouseout, so keep it in step with the zoom
        if (marker._original_icon) {
            marker._original_icon = resizedIcon(marker._original_icon, width, height);
        }
        const icon = marker.getIcon();
        if (!icon || typeof icon === "string" || icon.scaledSize?.width === width) {
            return;
        }
        marker.setIcon(resizedIcon(icon, width, height));
    });
};

/**
 * @param {any} mapWrap
 * @returns {void}
 */
const watchMap = (mapWrap) => {
    // Filter and pan re-queries re-plot the pins at their base size
    const setMarkers = mapWrap.set_markers.bind(mapWrap);
    mapWrap.set_markers = (...args) => {
        setMarkers(...args);
        scalePins(mapWrap);
    };
    // oilib fires oimap_idle once, after the first plot; zooms come from the map itself
    mapWrap.container.addEventListener(
        "oimap_idle",
        () => {
            scalePins(mapWrap);
            google.maps.event.addListener(mapWrap.vendor_implementation.map, "zoom_changed", () => scalePins(mapWrap));
        },
        { once: true },
    );
};

document.querySelectorAll("[data-pin-zoom-scale]").forEach((mapElement) => {
    const loadedMap = window.oi?.loaded?.map?.[mapElement.id];
    if (loadedMap) {
        watchMap(loadedMap);
    } else {
        window.addEventListener(`oi.loaded.map.${mapElement.id}`, () => watchMap(window.oi.loaded.map[mapElement.id]), {
            once: true,
        });
    }
});
